import { ConfigProps, Endpoints, RpcResponse, ServerConfig } from '../../types/requestTypes';
import cookie from 'js-cookie';
import { v4 } from 'uuid';
import { capability, getSuiteEndpoint } from '../config/capabilities';
import axios, { AxiosRequestHeaders } from 'axios';
import { getServerConfig } from '../config/config';

class RpcClientFactory {
    private stickySessionId = 'JSESSIONID';

    private endpoints = {
        PC: '',
        BC: ''
    } as Endpoints;

    private baseHeaders = {
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json',
        'Accept-Language': 'da-DK',
        'X-Top-Appname': 'local',
        'X-Top-Host': 'wso2dscm321.topdanmark.local',
        'X-Top-Port': '80'
    } as AxiosRequestHeaders;

    constructor(serverConfig: ServerConfig) {
        this.baseHeaders = { ...this.baseHeaders, ...serverConfig.headers };
        this.endpoints = { ...this.endpoints, ...serverConfig.endpoints };
    }

    private manageStickySessionCookie() {
        if (!cookie.get(this.stickySessionId)) {
            cookie.set(this.stickySessionId, v4());
        }
    }

    private getJsonRPCid() {
        const id = cookie.get(this.stickySessionId);
        if (id) {
            return `${id.substring(id.lastIndexOf('-') + 1)}_${this.baseHeaders['X-Top-Appname']}`;
        }
        return this.baseHeaders['X-Top-Appname'];
    }

    private static ensureSingleTrailingSlash(url: string) {
        return `${url}/`.replace(/(\/){2,}$/, '/');
    }

    private getBaseUrl(service: capability, openApi: boolean) {
        let baseUrl = getSuiteEndpoint(service, this.endpoints);
        if (openApi) {
            baseUrl = baseUrl.replace('/proxy/v2/', '/proxy/v2/openapi/');
        }
        return RpcClientFactory.ensureSingleTrailingSlash(baseUrl);
    }

    public setEndpoints(endpoints: Endpoints) {
        this.endpoints.PC = endpoints.PC;
        this.endpoints.BC = endpoints.BC;
    }

    public addTraceHeaders(traceHeaders: { jobNumber?: string; accountNumber?: string }) {
        if (traceHeaders.jobNumber) {
            this.baseHeaders['x-top-pc-job-number'] = traceHeaders.jobNumber;
        }
        if (traceHeaders.accountNumber) {
            this.baseHeaders['x-top-pc-account-number'] = traceHeaders.accountNumber;
        }
    }

    public async rpcCall<T>(
        service: capability,
        method: string,
        params: unknown[],
        headers: AxiosRequestHeaders,
        openApi = false
    ): Promise<T> {
        this.manageStickySessionCookie();
        const payload = {
            id: this.getJsonRPCid(),
            jsonrpc: '2.0',
            method,
            params
        };

        headers = { 'X-TOP-TRACEID': v4(), 'x-top-jsonrpc-method': method, ...this.baseHeaders, ...headers };
        const url = `${this.getBaseUrl(service, openApi)}${service}`;
        try {
            const response = await axios.post<RpcResponse<T>>(url, payload, {
                headers
            });
            return response.data.result;
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
}

// Singleton
let instance: RpcClientFactory;
export default {
    /**
     * Create an instance of the RPC Edge Client
     * @param config
     */
    createInstance: (config: ConfigProps) => {
        const serverConfig = getServerConfig(config);
        instance = new RpcClientFactory(serverConfig);
    },
    getInstance: () => {
        if (instance) {
            return instance;
        }
        throw new Error('RPC Client not loaded');
    }
};
